/**
 * The client picker, once, for two purposes that want different clients.
 *
 * Six screens fetched the client list independently and all six asked
 * `/clients` for `limit: 200`. That endpoint caps limit at 200 and there are
 * more clients than that, so every picker in the application was showing an
 * alphabetical prefix and stopping silently somewhere in the M's. `/clients/
 * options` exists for this: unpaginated, four columns, filtered server-side.
 *
 * `purpose` is a real parameter and not a style choice. The two sets differ:
 *
 * * **work** — logging a call, tracking a candidate, raising a requirement.
 *   Offers PIPELINE and ACTIVE, and PENDING as well as APPROVED. Ringing a
 *   prospect is what pipeline means, and blocking recruiter work while
 *   somebody waits for an approval costs more than the problem it prevents.
 * * **billing** — raising an invoice. Offers ACTIVE and INACTIVE, and only
 *   APPROVED. A final invoice arrives after a relationship has ended, so
 *   excluding dormant clients would strand every receivable on a closed
 *   account; and approval is the one thing that gates money.
 *
 * A rejected client is offered by neither. That is the difference between
 * rejecting one and leaving it pending.
 *
 * `attachedId` keeps a client rendered whatever its status, for the record
 * being edited. Without it, setting a client inactive would blank the client
 * on every historical call and invoice naming it — the form would show an
 * empty select, and saving would write the emptiness back.
 */
import { useQuery } from '@tanstack/react-query';
import { client } from '../api/client';
import type { ClientApprovalStatus, ClientLifecycle } from '../types';

export interface ClientOption {
  id: string;
  name: string;
  approvalStatus: ClientApprovalStatus;
  lifecycle: ClientLifecycle;
}

export function useClientOptions(
  purpose: 'work' | 'billing',
  attachedId?: string | null,
  includeImported = true,
) {
  return useQuery<ClientOption[]>({
    // attachedId is in the key: two records being edited in turn need two
    // answers, and reusing the first would drop the second's own client.
    queryKey: ['client-options', purpose, attachedId ?? null, includeImported],
    queryFn: async () =>
      (
        await client.get('/api/v1/clients/options', {
          params: {
            purpose,
            includeImported,
            ...(attachedId ? { includeId: attachedId } : {}),
          },
        })
      ).data,
  });
}

/** What to show after a name, when the status is worth saying out loud. */
function suffix(option: ClientOption): string {
  if (option.approvalStatus === 'pending') return ' · awaiting approval';
  if (option.lifecycle === 'pipeline') return ' · prospect';
  if (option.lifecycle === 'inactive') return ' · inactive';
  return '';
}

export default function ClientSelect({
  purpose,
  value,
  onChange,
  attachedId,
  includeImported = true,
  id,
  className = 'input',
  required = false,
  placeholder = 'Choose a client…',
}: {
  purpose: 'work' | 'billing';
  value: string;
  onChange: (value: string) => void;
  /** The client already on the record being edited, if any. */
  attachedId?: string | null;
  /**
   * Whether the names the importer created are offered.
   *
   * True for a picker, which is choosing a client for a new record and has to
   * reach any of them. False for a filter, which narrows a list somebody is
   * reading and where several hundred spreadsheet spellings drown the dozen
   * clients anybody typed in.
   */
  includeImported?: boolean;
  id?: string;
  className?: string;
  required?: boolean;
  placeholder?: string;
}) {
  const options = useClientOptions(
    purpose,
    attachedId ?? value ?? null,
    includeImported,
  );

  return (
    <select
      id={id}
      className={className}
      value={value}
      required={required}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{placeholder}</option>
      {(options.data ?? []).map((option) => (
        <option key={option.id} value={option.id}>
          {option.name}
          {suffix(option)}
        </option>
      ))}
    </select>
  );
}
